import type { TFunction } from 'i18next'
import {
  newNotifyAdminAction,
  slugifyFieldName,
  type CollectFieldType,
  type NotifyAdminAction,
} from './actionsSchemas'

/**
 * Situations pré-configurées pour « Me prévenir d'un appel ».
 *
 * Le client choisit une situation métier ; le preset remplit d'un coup le
 * nom, le scénario de déclenchement, la réponse de l'agent, les champs à
 * collecter et le template du message. Plus aucun champ vide à l'ouverture.
 *
 * DEUX LANGUES cohabitent, selon qui lit le texte :
 *  - `t`      (langue de l'INTERFACE) → nom de l'action, libellés des
 *             champs, template du message : c'est l'admin qui les lit,
 *             dans son SMS ou son email.
 *  - `tAgent` (langue de l'AGENT) → scénario de déclenchement (consommé
 *             par le LLM) et réponse de l'agent (entendue par l'appelant).
 *
 * Une clinique bilingue a donc légitimement un SMS en français et un agent
 * qui répond en anglais — forcer une seule langue casserait l'un des deux.
 *
 * Le contenu appliqué devient de la donnée : changer la langue de
 * l'interface ensuite ne le réécrit jamais (le client a pu le modifier).
 */

export type NotifyPresetId =
  | 'take_message'
  | 'callback_request'
  | 'emergency'
  | 'custom'

export const NOTIFY_PRESET_IDS: NotifyPresetId[] = [
  'take_message',
  'callback_request',
  'emergency',
  'custom',
]

/** Champs collectés par situation : type + obligatoire, libellé traduit à part. */
const PRESET_FIELDS: Record<
  Exclude<NotifyPresetId, 'custom'>,
  { key: string; type: CollectFieldType; required: boolean }[]
> = {
  take_message: [
    { key: 'caller_name', type: 'text', required: true },
    { key: 'reason', type: 'text', required: true },
    { key: 'callback_number', type: 'phone', required: true },
  ],
  callback_request: [
    { key: 'caller_name', type: 'text', required: true },
    { key: 'callback_number', type: 'phone', required: true },
    { key: 'preferred_time', type: 'text', required: false },
  ],
  emergency: [
    { key: 'caller_name', type: 'text', required: true },
    { key: 'callback_number', type: 'phone', required: true },
    { key: 'issue', type: 'text', required: true },
  ],
}

/**
 * Construit une action complète à partir d'une situation.
 * `t` = langue de l'interface, `tAgent` = langue de l'agent.
 */
export function buildPresetAction(
  presetId: NotifyPresetId,
  t: TFunction,
  tAgent: TFunction,
  recipient?: { adminPhone?: string; adminEmail?: string },
): NotifyAdminAction {
  // Le destinataire est connu du produit (compte connecté, lignes de
  // l'entreprise) : il est posé d'office, y compris en situation libre.
  const base: NotifyAdminAction = {
    ...newNotifyAdminAction(),
    admin_phone: recipient?.adminPhone ?? '',
    admin_email: recipient?.adminEmail ?? '',
  }

  if (presetId === 'custom') {
    // Situation libre : le reste du formulaire demeure vierge.
    return base
  }

  const key = (suffix: string) =>
    `manager.aiAgents.notifyPresets.${presetId}.${suffix}`

  // Libellés des champs dans la langue de l'interface — ils apparaissent
  // dans le message que l'admin reçoit.
  const collect_fields = PRESET_FIELDS[presetId].map((field) => {
    const label = t(key(`fields.${field.key}`))
    return {
      // `id` EST le slug du champ dans ce produit : c'est lui qui sert de
      // clé d'interpolation dans le template (cf. handleNameChange).
      id: slugifyFieldName(label),
      name: label,
      type: field.type,
      required: field.required,
    }
  })

  // Le template référence les slugs dérivés des libellés traduits : les
  // deux viennent de la même langue, ils restent donc cohérents.
  //
  // Syntaxe : accolade SIMPLE `{slug}` — c'est celle qu'interpolent
  // l'aperçu et le backend. Les `{{...}}` du catalogue i18n ne sont que
  // des emplacements i18next, remplacés ici par la variable finale.
  const slots: Record<string, string> = { call_time: '{call_time}' }
  PRESET_FIELDS[presetId].forEach((field, index) => {
    slots[field.key] = `{${collect_fields[index].id}}`
  })

  return {
    ...base,
    name: t(key('name')),
    scenario: tAgent(key('scenario')),
    agent_response: tAgent(key('agentResponse')),
    collect_fields,
    message_template: t(key('template'), slots),
    // Défauts sensés : les deux canaux, et le compte-rendu plutôt que
    // l'interruption pendant l'appel (le temps réel reste un cas d'urgence).
    notification_method: 'both',
    timing: presetId === 'emergency' ? 'realtime' : 'post_call',
  }
}

/**
 * Aperçu du message final, variables remplacées par des valeurs d'exemple —
 * le client voit ce qu'il recevra vraiment, pas une syntaxe à moustaches.
 */
export function renderTemplatePreview(
  template: string,
  collectFields: { name: string }[],
  t: TFunction,
): string {
  let out = template
  collectFields.forEach((field) => {
    const slug = slugifyFieldName(field.name)
    const sample = t(`manager.aiAgents.notifyPresets.samples.${slug}`, {
      defaultValue: t('manager.aiAgents.notifyPresets.samples.fallback'),
    })
    // Les deux syntaxes, comme pour les variables système ci-dessous : le
    // template stocké par les presets est en accolades SIMPLES ({slug}) —
    // ne chercher que {{slug}} laissait les champs collectés bruts dans
    // l'aperçu, précisément ce que cette fonction doit éviter.
    out = out.replaceAll(`{{${slug}}}`, sample).replaceAll(`{${slug}}`, sample)
  })
  // Variables système restantes
  const system: Record<string, string> = {
    call_time: t('manager.aiAgents.notifyPresets.samples.call_time'),
    agent_name: t('manager.aiAgents.notifyPresets.samples.agent_name'),
    caller_phone: t('manager.aiAgents.notifyPresets.samples.callback_number'),
  }
  Object.entries(system).forEach(([k, v]) => {
    out = out.replaceAll(`{{${k}}}`, v).replaceAll(`{${k}}`, v)
  })
  return out
}
